import { useDatabaseContent } from '@databases/database-state';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  ResolvedContentReference,
  PageReferenceOrContentId,
  ResolvedPageReference,
} from '@databases/types/confluence';
import {
  DatabaseField,
  ParentPageType,
  DatabaseFieldType,
} from '@databases/types/database';
import { isPageReference, pageToReference } from '@databases/utils/confluence/utils';
import { isNonEmptyEntryLinkValue } from '@databases/utils/database/field-types/entryLink';
import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  getConvertedDatabaseValue,
  getEntryPageReference,
} from '@databases/utils/database/utils';
import { loadContentById, loadContentRef } from '@databases/utils/redux/content/actions';
import {
  selectContentByIdOptional,
  selectContentByIdStatus,
  selectContentRefOptional,
  selectContentRefStatus,
} from '@databases/utils/redux/content/selectors';
import {
  selectDatabaseByContentIdRequired,
  selectDatabaseByIdOptional,
} from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import { useCallback } from 'react';

export function useDynamicParent(): {
  isPending: boolean;
  parentRef: ResolvedContentReference | undefined;
} {
  const dispatch = defaultStoreUtils.useDispatch();

  const { parentContentId } = useDatabaseContent();
  const contentId = parentContentId ?? undefined;

  useCallbackEffect(
    useCallback(async () => {
      if (contentId) {
        await dispatch(loadContentById(contentId));
      }
    }, [contentId, dispatch])
  );

  const [isPending, parentRef] = defaultStoreUtils.useEqualSelector((state) => {
    const parentItemById = selectContentByIdOptional(state, {
      contentId,
    });
    const parentItemByIdStatus = selectContentByIdStatus(state, {
      contentId,
    });
    const parentItemByIdIsPending =
      !!contentId &&
      [undefined, AsyncOperationStatus.INITIAL, AsyncOperationStatus.PENDING].includes(
        parentItemByIdStatus?.status
      );

    return [
      parentItemByIdIsPending,
      parentItemById && parentItemById.type !== 'blogpost'
        ? {
            type: parentItemById.type,
            spaceKey: parentItemById.space!.key,
            contentTitle: parentItemById.title,
            contentId: parentItemById.id,
          }
        : undefined,
    ] as const;
  });
  return { isPending, parentRef };
}

/**
 * If the page field is configured to allow creating pages under a location based on an entry link field
 * (parentPageType is FIELD and the type of the field referenced by parentPageFieldId is DATABASE_ENTRY), this hook
 * will return a reference to the entry page of the referenced entry. If the referenced database is not loaded yet,
 * the hook will make sure to load it.
 * It's no longer possible to configure a page field like this, but this is kept
 * for backwards compatibility reasons.
 * @return A page reference if the field parent page references an entry link field, the referenced
 * database has been loaded, and the referenced entry has an entry page. Otherwise, undefined is returned.
 */
function useEntryParentPageReference({
  contentId,
  field,
  entryId,
}: {
  contentId?: string;
  field: DatabaseField;
  entryId?: string;
}): { isPending: boolean; pageRef: PageReferenceOrContentId | undefined } {
  const [refPage] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft =
      contentId != null
        ? selectDatabaseByContentIdRequired(state, {
            contentId,
          })
        : undefined;
    const entry =
      databaseDraft && entryId != null
        ? getDatabaseEntryOptional(databaseDraft, entryId)
        : undefined;
    const refField =
      databaseDraft && field.settings.parentPageType === ParentPageType.FIELD
        ? getDatabaseFieldOptional(databaseDraft, field.settings.parentPageFieldId)
        : undefined;
    const refValue =
      databaseDraft && entry && refField
        ? getConvertedDatabaseValue(databaseDraft, refField, entry, state)
        : undefined;
    const refDatabaseValue =
      refValue && refValue.type === DatabaseFieldType.ENTRY_LINK && refValue.value
        ? refValue.value
        : undefined;
    const refDatabase = selectDatabaseByIdOptional(state, {
      spaceKey: refField?.settings.databaseSpace?.key,
      id: refField?.settings.databaseId,
    });
    const refDatabaseDraft =
      refDatabase &&
      selectDatabaseByContentIdRequired(state, {
        contentId: refDatabase.contentId,
      });
    const refEntry =
      refDatabaseDraft &&
      refDatabaseValue &&
      refDatabaseValue[0] &&
      isNonEmptyEntryLinkValue(refDatabaseValue[0]) &&
      getDatabaseEntryOptional(refDatabaseDraft, refDatabaseValue[0].entryId);
    const refPage =
      refDatabaseDraft &&
      refEntry &&
      getEntryPageReference(refDatabaseDraft, refEntry, state);
    return [refPage] as const;
  });

  return {
    isPending: false,
    pageRef: refPage && !refPage.create ? refPage : undefined,
  };
}

/**
 * This hook returns a page reference to the configured parent page of this field. Returns undefined if no valid parent page is configured,
 * or if the reference is still loading (because a referenced database has to be loaded).
 */
function useParentPageReference({
  contentId,
  field,
  entryId,
}: {
  contentId?: string;
  field: DatabaseField;
  entryId?: string;
}): {
  isPending: boolean;
  pageRef: PageReferenceOrContentId | undefined;
} {
  const entryPageReference = useEntryParentPageReference({ contentId, field, entryId });

  return defaultStoreUtils.useEqualSelector((state) => {
    const database =
      contentId != null
        ? selectDatabaseByContentIdRequired(state, {
            contentId,
          })
        : undefined;
    const entry =
      database && entryId != null
        ? getDatabaseEntryOptional(database, entryId)
        : undefined;

    if (!database || !entry || field.settings.creatable === false) {
      return { isPending: false, pageRef: undefined };
    } else if (field.settings.parentPageType === ParentPageType.FIELD) {
      const refField =
        database && field.settings.parentPageType === ParentPageType.FIELD
          ? getDatabaseFieldOptional(database, field.settings.parentPageFieldId)
          : undefined;
      if (refField?.type === DatabaseFieldType.ENTRY_LINK) {
        if (entryPageReference.isPending || entryPageReference.pageRef) {
          return entryPageReference;
        }
      } else if (refField?.type === DatabaseFieldType.PAGE_LINK) {
        const value = getConvertedDatabaseValue(
          database,
          refField as DatabaseField<DatabaseFieldType.PAGE_LINK>,
          entry,
          state
        ).value;

        if (
          value.length > 0 &&
          (('type' in value[0] && isPageReference(value[0]) && !value[0].create) ||
            ('contentId' in value[0] && !('type' in value[0])))
        ) {
          return { isPending: false, pageRef: value[0] };
        }
      }
    }

    return { isPending: false, pageRef: field.settings.parentPage };
  });
}

/**
 * This hook returns the resolved page reference that should be used as the parent page for new pages created by this field. It
 * returns undefined if no valid page is configured or if the page is still loading. It makes sure to load the page if it is not
 * loaded yet.
 */
export function useFixedParentPage({
  contentId,
  field,
  entryId,
}: {
  contentId?: string;
  field: DatabaseField;
  entryId?: string;
}): { isPending: boolean; pageRef: ResolvedPageReference | undefined } {
  const { isPending: parentPageIsPending, pageRef: parentPageRef } =
    useParentPageReference({ contentId, field, entryId });

  const dispatch = defaultStoreUtils.useDispatch();

  useCallbackEffect(
    useCallback(async () => {
      if (parentPageRef && 'contentId' in parentPageRef && parentPageRef.contentId) {
        await dispatch(loadContentById(parentPageRef.contentId));
      } else if (parentPageRef && 'contentTitle' in parentPageRef) {
        await dispatch(loadContentRef(parentPageRef));
      }
    }, [dispatch, parentPageRef])
  );

  const [isPending, pageRef] = defaultStoreUtils.useEqualSelector((state) => {
    const parentPageById = selectContentByIdOptional(state, {
      contentId:
        parentPageRef && 'contentId' in parentPageRef
          ? parentPageRef.contentId
          : undefined,
    });
    const parentPageByIdStatus = selectContentByIdStatus(state, {
      contentId:
        parentPageRef && 'contentId' in parentPageRef
          ? parentPageRef.contentId
          : undefined,
    });

    const parentPageByTitle = selectContentRefOptional(state, {
      contentRef:
        parentPageRef && 'contentTitle' in parentPageRef ? parentPageRef : undefined,
    });
    const parentPageByTitleStatus = selectContentRefStatus(state, {
      contentRef:
        parentPageRef && 'contentTitle' in parentPageRef ? parentPageRef : undefined,
    });

    const parentPageByIdIsPending =
      !!parentPageRef &&
      'contentId' in parentPageRef &&
      [undefined, AsyncOperationStatus.INITIAL, AsyncOperationStatus.PENDING].includes(
        parentPageByIdStatus?.status
      );
    const parentPageByTitleIsPending =
      !!parentPageRef &&
      'contentTitle' in parentPageRef &&
      [undefined, AsyncOperationStatus.INITIAL, AsyncOperationStatus.PENDING].includes(
        parentPageByTitleStatus?.status
      );

    return [
      parentPageIsPending || parentPageByIdIsPending || parentPageByTitleIsPending,
      parentPageById && parentPageById.type === 'page'
        ? pageToReference(parentPageById)
        : parentPageByTitle?.type === 'page'
        ? parentPageByTitle
        : undefined,
    ] as const;
  });

  return { isPending, pageRef };
}
